import axios from "axios";
import {
  API_URL,
  clearSession,
  getAuthToken,
  getRefreshToken,
} from "../Constants";

/**
 * Session storage, automatic access-token renewal and logout for the CMS.
 *
 * The backend rotates the refresh token on every successful refresh. All
 * renewal paths therefore converge here, and concurrent 401 responses share
 * one in-flight request. Without that single-flight guard, two failed requests
 * would submit the same refresh token; the first would rotate it and the second
 * would incorrectly end a healthy session.
 */

/** Fired when the session has ended and the operator must sign in again. */
export const SESSION_EXPIRED_EVENT = "dpa:session-expired";

/** Fired when a session begins or is renewed, so watchers can re-arm. */
export const SESSION_RENEWED_EVENT = "dpa:session-renewed";

/**
 * Reasons a session ended. Kept distinct because they deserve different words:
 * "your session timed out" is reassuring, "you are not authorised" is alarming,
 * and showing the wrong one sends people to the helpdesk for no reason.
 */
export const EXPIRY_REASON = {
  TIMEOUT: "timeout", // tokenExpiry passed
  UNAUTHORIZED: "unauthorized", // a request came back 401
};

let notified = false;
let refreshPromise = null;
let interceptorId = null;

/**
 * -----------------------------------------------------------------------------
 * CROSS-TAB REFRESH COORDINATION
 *
 * `refreshPromise` above makes renewal single-flight, but it is MODULE state -
 * one per tab, not one per browser. The backend rotates the refresh token on
 * every successful `/refresh`, so two tabs are enough to end a healthy session
 * while the operator is actively working:
 *
 *   1. both tabs cross the "renew soon" threshold on the same tick
 *   2. both read the same refresh token from localStorage and POST /refresh
 *   3. the first rotates it; the second submits a token that no longer exists
 *   4. the backend answers 401 "Invalid refresh token."
 *   5. 401 was treated as terminal -> "انتهت صلاحية الجلسة" mid-edit
 *
 * and because `notifySessionExpired` calls `clearSession`, the tab that was
 * being used is signed out along with the one that lost the race.
 *
 * Two independent defences, because either alone still leaves a window:
 *   - a localStorage lock, so normally only one tab calls /refresh at all; the
 *     others wait for the rotated token and reuse it.
 *   - `requestRefresh` retries ONCE when the token it sent has since been
 *     replaced in storage, which recovers the race even when the lock cannot be
 *     held (private mode, no storage quota, a tab killed mid-refresh).
 * -----------------------------------------------------------------------------
 */
const REFRESH_LOCK_KEY = "refreshLock";

/** How long a lock is honoured before it is assumed to be a dead tab's. */
const REFRESH_LOCK_TTL_MS = 20 * 1000;

const LOCK_POLL_MS = 150;

/** Identifies this tab for the lifetime of the page. */
const TAB_ID = `${Date.now().toString(36)}-${Math.random()
  .toString(36)
  .slice(2, 8)}`;

const readRefreshLock = () => {
  try {
    return JSON.parse(localStorage.getItem(REFRESH_LOCK_KEY) || "null");
  } catch (_error) {
    return null;
  }
};

/**
 * Claim the right to call `/refresh`.
 *
 * Write-then-verify rather than check-then-write: when two tabs both see no lock
 * and both write, the later write wins and the earlier tab's re-read shows
 * someone else's id. Exactly one tab therefore returns true.
 *
 * Returns true when storage is unavailable, so a browser that cannot hold a lock
 * still refreshes - falling back to the retry in `requestRefresh` - instead of
 * waiting forever for a lock it can never take.
 */
const acquireRefreshLock = () => {
  try {
    const existing = readRefreshLock();
    if (existing && Date.now() - existing.at < REFRESH_LOCK_TTL_MS) {
      return false;
    }

    localStorage.setItem(
      REFRESH_LOCK_KEY,
      JSON.stringify({ id: TAB_ID, at: Date.now() }),
    );
    return readRefreshLock()?.id === TAB_ID;
  } catch (_error) {
    return true;
  }
};

const releaseRefreshLock = () => {
  try {
    if (readRefreshLock()?.id === TAB_ID) {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    }
  } catch (_error) {
    // Nothing to release; the TTL retires the lock on its own.
  }
};

/**
 * Wait for the tab holding the lock to publish a rotated access token.
 *
 * Polls rather than listening for `storage`, because a `storage` event never
 * fires in the tab that made the change, and this has to behave correctly no
 * matter which tab turns out to be the holder.
 *
 * @returns {Promise<string|null>} the rotated access token, or null when waiting
 *          is pointless - the holder ended the session, or stopped existing.
 */
const waitForRotatedToken = async (previousToken) => {
  const deadline = Date.now() + REFRESH_LOCK_TTL_MS;

  while (Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, LOCK_POLL_MS));

    const current = getAuthToken();
    if (current && current !== previousToken) return current;

    // The holder concluded the session is over. Stop waiting and let the expiry
    // it has already raised stand.
    if (!getRefreshToken()) return null;

    const lock = readRefreshLock();
    if (!lock || Date.now() - lock.at >= REFRESH_LOCK_TTL_MS) return null;
  }

  return null;
};

const authClient = axios.create({
  baseURL: `${API_URL}/api/Auth`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

const authPayload = (responseOrBody) => {
  const body = responseOrBody?.data ?? responseOrBody;
  return body?.data && typeof body.data === "object" ? body.data : body;
};

const authError = (message, status = null) => {
  const error = new Error(message);
  error.status = status;
  return error;
};

/**
 * Store a login/refresh response atomically. The API currently returns a flat
 * LoginResponse; the nested fallback keeps the client ready for the standard
 * `{ success, data }` envelope without changing callers.
 */
export const saveSession = (responseOrBody) => {
  const payload = authPayload(responseOrBody);

  if (
    !payload?.token ||
    !payload?.refreshToken ||
    payload?.authenticated === false ||
    payload?.success === false
  ) {
    throw authError(
      payload?.error || payload?.message || "تعذّر إنشاء جلسة صالحة.",
    );
  }

  localStorage.setItem("token", payload.token);
  localStorage.setItem("tokenExpiry", payload.tokenExpiry ?? "");

  localStorage.setItem("refreshToken", payload.refreshToken);
  localStorage.setItem("refreshTokenExpiry", payload.refreshTokenExpiry ?? "");

  if (payload.companyId) {
    localStorage.setItem("userId", payload.companyId);
  }

  // `cmsPages` is the ONLY source that carries the sidebar's nested tree -
  // `GET /api/Auth/GetPageAccess` returns `nested: []` on every row. See
  // `getStoredCmsPages` in ../Constants.js for the full note. Guarded so a
  // refresh response (which has no cmsPages) cannot wipe what login stored.
  if (Array.isArray(payload.cmsPages) && payload.cmsPages.length > 0) {
    try {
      localStorage.setItem("cmsPages", JSON.stringify(payload.cmsPages));
    } catch (_error) {
      // Quota or a serialisation failure. The sidebar falls back to
      // GetPageAccess, so losing the cache is survivable; failing the login
      // over it would not be.
    }
  }

  return payload;
};

/**
 * Announce that the session is over.
 *
 * Guarded so that a screen firing six parallel requests, all of which 401,
 * raises ONE dialog rather than six. Reset by `markSessionRenewed`.
 */
export const notifySessionExpired = (reason = EXPIRY_REASON.TIMEOUT) => {
  if (notified) return;
  notified = true;

  clearSession();
  window.dispatchEvent(
    new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { reason } }),
  );
};

/** Called after a successful re-authentication. */
export const markSessionRenewed = () => {
  notified = false;
  window.dispatchEvent(new CustomEvent(SESSION_RENEWED_EVENT));
};

export const isSessionExpiredNotified = () => notified;

const storedExpiryTime = (key) => {
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw.trim());
  const normalised = hasZone ? raw : `${raw.replace(/\s+$/, "")}Z`;
  const t = new Date(normalised).getTime();
  return Number.isNaN(t) ? null : t;
};

/** `tokenExpiry` as a timestamp, or `null` when absent or unparseable. */
export const getTokenExpiryTime = () => storedExpiryTime("tokenExpiry");

/** Refresh-token expiry as a timestamp, or `null` when unavailable. */
export const getRefreshTokenExpiryTime = () =>
  storedExpiryTime("refreshTokenExpiry");

/**
 * Milliseconds until the token expires; negative once it has.
 *
 * The current backend includes `+04:00`. The explicit UTC fallback preserves
 * compatibility with older values that omitted a zone.
 */
export const msUntilExpiry = () => {
  const expiry = getTokenExpiryTime();
  return expiry === null ? null : expiry - Date.now();
};

export const hasSession = () => Boolean(getAuthToken());

const terminalRefreshFailure = (error) => {
  const status = error?.response?.status ?? error?.status ?? null;
  return status === 400 || status === 401 || status === 403;
};

/**
 * POST one refresh token and store whatever comes back.
 *
 * @param {string} refreshToken the credential to spend
 * @param {boolean} [allowRotationRetry] when the call is rejected, allow ONE
 *        further attempt with whatever token is in storage by then. See below.
 */
const requestRefresh = async (refreshToken, allowRotationRetry = true) => {
  try {
    // No bearer header: the access token may already be expired. The refresh
    // token in the body is the credential for this endpoint.
    const response = await authClient.post("/refresh", { refreshToken });
    const payload = authPayload(response);

    if (!payload?.token || !payload?.refreshToken) {
      throw authError(
        payload?.error ||
          payload?.message ||
          "لم تُرجع خدمة التجديد رموز جلسة صالحة.",
        response?.status ?? null,
      );
    }

    saveSession(payload);
    markSessionRenewed();
    return payload.token;
  } catch (error) {
    if (terminalRefreshFailure(error)) {
      const stored = getRefreshToken();

      // THE ROTATION RACE, RECOVERED.
      //
      // A 401 here does NOT prove the session is over. It also happens when
      // another tab spent this token a moment ago: the backend rotates on every
      // refresh, so the token we sent is now genuinely invalid while the session
      // itself is perfectly healthy - the successor is already in storage.
      //
      // The tell is that storage no longer holds what we submitted. Retry once
      // with the successor. Only once, and only on a changed value, so a truly
      // rejected credential still ends the session immediately rather than
      // looping.
      if (allowRotationRetry && stored && stored !== refreshToken) {
        return requestRefresh(stored, false);
      }

      notifySessionExpired(EXPIRY_REASON.UNAUTHORIZED);
    }
    throw error;
  }
};

const performRefresh = async () => {
  const previousAccessToken = getAuthToken();
  const refreshToken = getRefreshToken();
  const refreshExpiry = getRefreshTokenExpiryTime();

  if (!refreshToken) {
    const error = authError("لا يتوفر رمز لتجديد الجلسة.", 401);
    notifySessionExpired(EXPIRY_REASON.UNAUTHORIZED);
    throw error;
  }

  if (refreshExpiry !== null && refreshExpiry <= Date.now()) {
    const error = authError("انتهت صلاحية رمز تجديد الجلسة.", 401);
    notifySessionExpired(EXPIRY_REASON.TIMEOUT);
    throw error;
  }

  // Another tab is already renewing. Reuse its result rather than spending a
  // token that is about to be rotated out from under us.
  if (!acquireRefreshLock()) {
    const rotated = await waitForRotatedToken(previousAccessToken);
    if (rotated) {
      markSessionRenewed();
      return rotated;
    }
    // The holder produced nothing usable. Fall through and renew here - the
    // retry inside `requestRefresh` covers the token having moved on meanwhile.
  }

  try {
    // Re-read: the value may have been rotated while we waited for the lock.
    return await requestRefresh(getRefreshToken() ?? refreshToken);
  } finally {
    releaseRefreshLock();
  }
};

/**
 * Renew once, even when many requests receive 401 at the same time.
 */
export const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

/**
 * Revoke the refresh token server-side, then always clear the local session.
 * Logout remains successful from the operator's perspective even if the
 * network is unavailable; leaving local credentials behind would be worse.
 */
export const logoutSession = async () => {
  const refreshToken = getRefreshToken();
  const accessToken = getAuthToken();

  try {
    if (refreshToken) {
      await authClient.post(
        "/logout",
        { refreshToken },
        {
          headers: accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : undefined,
        },
      );
    }
    return true;
  } catch (_error) {
    return false;
  } finally {
    clearSession();
    notified = false;
  }
};

/**
 * Catch a 401 from ANY request, renew once and retry the original request.
 *
 * `services/apiClient.js` is the contract-aware client, but it is new: 258 call
 * sites across 64 files still call `axios` directly. A response interceptor on
 * the default axios instance covers every one of them without touching a single
 * file, which is the only realistic way to make session handling complete
 * rather than partial.
 *
 * Install once, at app start. Guarded so React's StrictMode double-invoke (or a
 * hot reload) cannot stack duplicate interceptors, which would fire the expiry
 * event twice per failed request.
 */
const isAuthRequest = (config) =>
  /\/api\/Auth\/(?:login|refresh|logout|forgotPassword)(?:[/?]|$)/i.test(
    config?.url ?? "",
  );

export const installSessionInterceptor = () => {
  if (interceptorId !== null) return interceptorId;

  interceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;

      if (
        error?.response?.status !== 401 ||
        !config ||
        config.__dpaRetriedAfterRefresh ||
        isAuthRequest(config)
      ) {
        if (
          error?.response?.status === 401 &&
          !isAuthRequest(config) &&
          (config?.__dpaRetriedAfterRefresh || !getRefreshToken())
        ) {
          notifySessionExpired(EXPIRY_REASON.UNAUTHORIZED);
        }
        return Promise.reject(error);
      }

      config.__dpaRetriedAfterRefresh = true;

      try {
        const token = await refreshSession();
        config.headers = {
          ...(config.headers ?? {}),
          Authorization: `Bearer ${token}`,
        };
        return axios(config);
      } catch (refreshError) {
        if (terminalRefreshFailure(refreshError)) {
          notifySessionExpired(EXPIRY_REASON.UNAUTHORIZED);
        }
        return Promise.reject(refreshError);
      }
    },
  );

  return interceptorId;
};

export const uninstallSessionInterceptor = () => {
  if (interceptorId === null) return;
  axios.interceptors.response.eject(interceptorId);
  interceptorId = null;
};
